import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { LogErrors } from './../../../../utilities/LogErrors'
import { useLanguage } from './../../../../utilities/LanguageContext'
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'

// Funciones personalizadas
import { GetCourses, ValidateLicense, UseLicense, GetCenterFreeLicensesStatus } from './../../../../utilities/BL/Student/Courses'
import { GetResults } from './../../../../utilities/BL/Student/Results'

// Componentes comunes generales
import { Header } from './../../../Header/V2/Header'
import { Section } from './../../Section'
import { Loading } from './../../../Common/Loading'
import { GoToTop } from './../../../Common/GoToTop'
import { Button } from './../../../Common/Button'
import { Result } from './../V2/Result'
import { Confirm } from './../../../Common/Modals/Confirm'
import { HELP_TYPE } from './../../../../utilities/EL/HelpType'

// Funciones comunes generales
import { GetCurrentCenter as BLGetCurrentCenter, GetNickname as BLGetNickname, GetZtudentDataToGames as BLGetZtudentDataToGames, SaveCourseToShow } from './../../../../utilities/BL/Navigation'
import { BodyWithSideMenu } from '../../BodyWithSideMenu'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'
import { TYPE_PROPOSITION_VALUE } from '../../../../utilities/EL/TypePropositionValue'
import { ROLES } from './../../../../utilities/EL/Roles'
import { CreateSession as BLCreateSession, GetGameStatus as BLGetGameStatus } from './../../../../utilities/BL/Games/Games'


const Ztudiant = ({ session, setSession, theme, setTheme, centerSelected, roleSelected }) => {
    const { translate } = useLanguage()
    const { showMessage } = useFloatMessage()
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(true)
    const [theTheme] = useState(theme)
    const [isGameActive, setIsGameActive] = useState(false)

    const [courses, setCourses] = useState(null)
    const [approvedCourses, setApprovedCourses] = useState(null)
    const [results, setResults] = useState(null)

    const [currentCenter, setCurrentCenter] = useState(null)
    const [nickname, setNickname] = useState(null)

    // --- Control de licencias al entrar a un curso ---
    const [isCheckingLicense, setIsCheckingLicense] = useState(false)
    const [showLicenseConfirmModal, setShowLicenseConfirmModal] = useState(false)
    const [pendingCourse, setPendingCourse] = useState(null) // { course, goToQuickAccessInstrument }
    // Si el centro está en modalidad de licencias gratuitas (GetCenterFreeLicensesStatus).
    // Por defecto false (fail-safe): mientras no se confirme lo contrario, se corre el
    // control de licencias al entrar a un curso.
    const [isFreeLicenses, setIsFreeLicenses] = useState(false)

    // Guarda el curso elegido y navega a /course. Separado de GoToNextView para poder
    // llamarlo tanto si la licencia ya estaba en uso como después de confirmar su activación.
    const EnterCourse = async (course, goToQuickAccessInstrument = false) => {
        try {
            const response = await SaveCourseToShow(session, course, goToQuickAccessInstrument)

            if (response.success) {
                setSession(response.data)
                navigate('/course')
            }
        } catch (error) {
            await LogErrors('EnterCourse - Ztudiant', error)
        }
    }

    const GoToNextView = async (course, goToQuickAccessInstrument = false) => {
        try {
            if (isFreeLicenses) {
                // Centro en modalidad de licencias gratuitas: se entra directo, sin validar
                // ni consumir ninguna licencia.
                await EnterCourse(course, goToQuickAccessInstrument)
                return
            }

            if (isCheckingLicense) return
            setIsCheckingLicense(true)

            const response = await ValidateLicense(course.id)

            if (!response?.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || 'No ha sido posible validar la licencia del curso.' })
                return
            }

            // El campo llega en minúsculas ("licenseused") en la respuesta real del servidor,
            // aunque el ejemplo documentado lo mostraba en PascalCase; se aceptan ambas formas
            // por seguridad ante el mismo desajuste de mayúsculas en otros entornos.
            const licenseUsed = response.data?.licenseused ?? response.data?.Licenseused

            if (licenseUsed) {
                // Ya se había usado una licencia para este curso: se entra directo, sin preguntar.
                await EnterCourse(course, goToQuickAccessInstrument)
            } else {
                setPendingCourse({ course, goToQuickAccessInstrument })
                setShowLicenseConfirmModal(true)
            }
        } catch (error) {
            await LogErrors('GoToNextView - Ztudiant', error)
        } finally {
            setIsCheckingLicense(false)
        }
    }

    const ConfirmUseLicense = async () => {
        try {
            if (!pendingCourse) return { success: true }

            const response = await UseLicense(pendingCourse.course.id)

            if (response?.success) {
                await EnterCourse(pendingCourse.course, pendingCourse.goToQuickAccessInstrument)
            } else {
                // Si el registro de la licencia falla, el curso NO debe mostrarse.
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible activar la licencia' })
            }

            // El modal se cierra en ambos casos: el error ya se comunicó como popup flotante.
            return { success: true }
        } catch (error) {
            await LogErrors('ConfirmUseLicense - Ztudiant', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'No fue posible activar la licencia' })
            return { success: true }
        }
    }

    const checkGameAccess = async () => {
        const centerId = session?.navigation?.center?.id;
        const response = await BLGetGameStatus(centerId, 'tiro-a-gol');

        if (response.success && response.data.isActive) {
            setIsGameActive(true)
            await GetZtudentDataToGames(1);
        } else {
            setIsGameActive(false)
            showMessage({
                type: MESSAGE_TYPE.WARNING,
                text: "Esta dinámica está inhabilitada por el momento.",
                duration: 3000
            });
        }
    }

    const GetZtudentDataToGames = async (idGame) => {
        try {
            setIsLoading(true);
            const responseData = await BLGetZtudentDataToGames(idGame);
            
            if (responseData.success) {
                const responseCreateSession = await BLCreateSession(responseData.data);
                if (responseCreateSession.success) {
                    const magicLink = responseCreateSession.data;
                    if (magicLink) window.location.href = magicLink;
                }
            }
        } catch (error) {
            await LogErrors('GetZtudentDataToGames - Ztudiant', error)
        } finally {
            setIsLoading(false)
        }
    }

    const GetListOfCourses = async (evaluations = []) => {
        try {
            const unique = new Set()
            evaluations.forEach(evaluation => {
                if (evaluation.idCourse !== undefined) unique.add(evaluation.idCourse)
            })
            return Array.from(unique).map(idCourse => ({ idCourse }))
        } catch (error) {
            await LogErrors('GetListOfCourses - Ztudiant', error)
        }
    }

    const LoadPage = async () => {
        try {
            document.body.classList.add('BodyComplete')
            const response = await GetCourses(session, centerSelected, roleSelected)
            
            if (response.success) {    
                const myCourses = response.data?.user?.courses?.myCourses
                setCourses(myCourses || null)
                const myApprovedCourses = response.data?.user?.courses?.myApprovedCourses
                setApprovedCourses(myApprovedCourses || null)
                
                const results = await GetResults(response.data)
                if (results.success) {
                    const evaluations = results?.data?.evaluations
                    setResults(evaluations ? await GetListOfCourses(evaluations) : null)
                    setSession(results.data)
                } else {
                    setSession(response.data)
                }
            } else {
                console.log(response?.message)
                const results = await GetResults(session)
                if (results.success) {
                    const evaluations = results?.data?.evaluations
                    setResults(evaluations || null)
                    setSession(results.data)
                }
            }

            const responseCenter = await BLGetCurrentCenter()
            if (responseCenter.success) {
                let centerId = parseInt(responseCenter?.data?.id)
                responseCenter.data.id = centerId
                setCurrentCenter(responseCenter.data)

                // Comentado: esta llamada consulta un servicio interno separado
                // (REACT_APP_INTERNAL_API_URL) que sigue apuntando a localhost y no está
                // disponible fuera de la máquina del desarrollador original, por lo que
                // marca error y agrega tiempo de espera en cada carga del dashboard (ver
                // rama "comentar tiro a gol"). isGameActive queda en false (su valor por
                // defecto), como si la dinámica estuviera deshabilitada.
                // const gameStatus = await BLGetGameStatus(centerId, 'tiro-a-gol');
                //
                // setIsGameActive(gameStatus.success && gameStatus.data.isActive);
            }

            const responseNickname = await BLGetNickname()
            if (responseNickname.success) setNickname(responseNickname?.data)

            // Estado de licencias gratuitas del centro: determina si al entrar a un curso se
            // corre el control de licencias (ValidateLicense/UseLicense) o se entra directo.
            const responseFreeLicenses = await GetCenterFreeLicensesStatus()
            if (responseFreeLicenses.success) {
                // Se aceptan ambas grafías por seguridad, ante el mismo desajuste de mayúsculas
                // visto en otros endpoints (documentación en PascalCase, respuesta en camelCase).
                const freeLicenses = responseFreeLicenses.data?.isFreeLicenses ?? responseFreeLicenses.data?.IsFreeLicenses
                setIsFreeLicenses(freeLicenses === true)
            } else {
                await LogErrors('LoadPage - Ztudiant (GetCenterFreeLicensesStatus)', responseFreeLicenses)
            }

        } catch (error) {
            await LogErrors('LoadPage - Ztudiant', error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        LoadPage()
    }, [])

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <>
                <BodyWithSideMenu page={WEBPAGES.ZTUDENT.DASHBOARD} theme={theme} propositionValue={TYPE_PROPOSITION_VALUE.ZTUDENT} idHelp={HELP_TYPE.ZTUDENT.DASHBOARD} role={ROLES.ZTUDENT}>
                    <Header theme={theme} setTheme={setTheme} propositionValue={TYPE_PROPOSITION_VALUE.ZTUDENT} idHelp={HELP_TYPE.ZTUDENT.DASHBOARD} />
                    
                    <main className='Container Dashboard--V2 Container-FullHeight Fonts--PlusJakartaSans'>
                        <p className='Dashboard--V2__Title'>{translate('labels.dashboard.courses.welcome')} <span className='Dashboard--V2__Text--Highlight'>{(nickname && nickname !== '') ? nickname : `${session.user.name} ${session.user.lastName}`} <i className='bi bi-emoji-smile'></i></span></p>
                        {session?.navigation?.center?.name && (
                            <p className='Dashboard--V2__Text Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Centro: {session.navigation.center.name}</p>
                        )}
                        {session?.navigation?.username && (
                            <p className='Dashboard--V2__Text Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Usuario: {session.navigation.username}</p>
                        )}
                        
                        <p className='Dashboard--V2__SpaceUp'>Ingresa a la sección que desees</p>

                        {/* <section className='Dashboard--V2__SpaceUp Dashboard--V2__Sections'>
                            <p className='Dashboard--V2__Sections__Title'>Celebremos el día del estudiante</p>
                            <p className='Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Con la dinámica que tenemos preparada para ti</p>
                            
                            <div className='Dashboard--V2__Sections__List'>
                                <div className={`Dashboard--V2__Sections__List__Items--Ztudent ${isGameActive ? 'Dashboard--V2__Sections__List__Items--Green' : 'Dashboard--V2__Sections__List__Items--Gray'}`} onClick={checkGameAccess}>
                                    <p className='Dashboard--V2__Sections__List__Items--Ztudent__Subtitle'><i className='bi bi-controller'></i> Dinámicas</p>
                                    <p className='Dashboard--V2__Sections__List__Items--Ztudent__Title h-auto'>Tiro a gol (1 vs 1)</p>
                                    <p className='Dashboard--V2__Sections__List__Items--Ztudent__Title small mb-0 pb-0 h-auto'>{isGameActive ? '¡Entra ahora!' : 'Próximamente'}</p>
                                    
                                    <button type='button' className='Btn__Ztudia--PrimaryOutline Dashboard--V2__Sections__List__Items--Ztudent__Buttons'>
                                        <i className={isGameActive ? 'bi bi-arrow-right' : 'bi bi-lock-fill'}></i>
                                    </button>
                                </div>
                            </div>
                        </section> */}

                        <section className='Dashboard--V2__SpaceUp Dashboard--V2__Sections'>
                            {courses && (
                                courses.length > 0 ? (
                                    <>
                                        <p className='Dashboard--V2__Sections__Title'>{translate('labels.dashboard.courses.title')}</p>
                                        <p className='Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Total: {courses.length}</p>

                                        <div className='Dashboard--V2__Sections__List'>
                                            {courses.map((course, index) => (
                                                course && (
                                                    <div key={index} className='Dashboard--V2__Sections__List__Items--Ztudent Dashboard--V2__Sections__List__Items--Pink' onClick={() => GoToNextView(course)}>
                                                        {course.tileIconClass && course.tileIconClass !== '' && (
                                                            <figure className='Dashboard--V2__Sections__List__Items--Ztudent__Icon'>
                                                                <i className={`${course.tileIconClass}`}></i>
                                                            </figure>
                                                        )}
                                                        <p className='Dashboard--V2__Sections__List__Items--Ztudent__Subtitle'><i className='bi bi-file-earmark'></i> {course.type === 1 ? translate('labels.dashboard.tiles.examType.certification') : translate('labels.dashboard.tiles.examType.custom')}</p>
                                                        <p className='Dashboard--V2__Sections__List__Items--Ztudent__Title'>{`${course.name}${course?.level && ` nivel ${course.level}` }`}</p>
                                                        <button type='button' className='Btn__Ztudia--PrimaryOutline Dashboard--V2__Sections__List__Items--Ztudent__Buttons' onClick={() => GoToNextView(course)}>
                                                            <i className='bi bi-arrow-right'></i>
                                                        </button>
                                                    </div>
                                                )
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <p>{translate('labels.dashboard.courses.empty')}</p>
                                )
                            )}
                        </section>

                        <section className='Dashboard--V2__SpaceUp Dashboard--V2__Sections'>
                            {results && (
                                results.length > 0 ? (
                                    <>
                                        <p className='Dashboard--V2__Sections__Title'>Mis resultados</p>
                                        <p className='Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Total: {results.length}</p>
                                        <div className='Dashboard--V2__Sections__List'>
                                            {results.map((result, index) => (
                                                result && (<Result key={index} session={session} result={result} />)
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <p>Actualmente no tienes resultados.</p>
                                )
                            )}
                        </section>
                    </main>
                </BodyWithSideMenu>

                {showLicenseConfirmModal && pendingCourse && (
                    <Confirm
                        message={`Al ingresar al curso ${pendingCourse.course.name} utilizarás una licencia ¿estás de acuerdo?`}
                        actionToYesButton={ConfirmUseLicense}
                        textYesButton='Sí, utilizar licencia'
                        textNoButton='No'
                        setShowModal={setShowLicenseConfirmModal}
                        setConfirmData={setPendingCourse}
                    />
                )}
                </>
            )}
        </>
    )
}

export { Ztudiant };









// import { useState, useEffect } from 'react'
// import { useNavigate } from 'react-router'

// import { LogErrors } from './../../../../utilities/LogErrors'
// import { useLanguage } from './../../../../utilities/LanguageContext'

// //Funciones personalizadas
// import { GetCourses } from './../../../../utilities/BL/Student/Courses'
// import { GetResults } from './../../../../utilities/BL/Student/Results'


// //Componentes comunes generales
// //import { Header } from './../../../Header/Header'
// import { Header } from './../../../Header/V2/Header'
// import { Section } from './../../Section'
// import { Loading } from './../../../Common/Loading'
// import { GoToTop } from './../../../Common/GoToTop'
// import { Button } from './../../../Common/Button'
// //import { Result } from './../Result'
// import { Result } from './../V2/Result'
// import { HELP_TYPE } from './../../../../utilities/EL/HelpType'

// //Componentes del proyecto
// //import { Tile } from './../Tile'
// //import { Credits } from './../../../Common/Credits/Credits'

// // Funciones comunes generales
// import { GetCurrentCenter as BLGetCurrentCenter, GetNickname as BLGetNickname, SaveCourseToShow } from './../../../../utilities/BL/Navigation'
// import { SideMenu } from '../../../Menu/V2/SideMenu'
// import { BodyWithSideMenu } from '../../BodyWithSideMenu'
// import { WEBPAGES } from './../../../../utilities/EL/WebPages'
// import { TYPE_PROPOSITION_VALUE } from '../../../../utilities/EL/TypePropositionValue'

// import { ROLES } from './../../../../utilities/EL/Roles'


// const Ztudiant = ({ session, setSession, theme, setTheme, centerSelected, roleSelected }) => {
//     const { translate } = useLanguage() // Obtén la función de traducción desde el contexto
//     const navigate = useNavigate()

//     const [isLoading, setIsLoading] = useState(true)
//     const [theTheme] = useState(theme)

//     // const [courses, setCourses] = useState([])
//     // const [approvedCourses, setApprovedCourses] = useState([])
//     // const [results, setResults] = useState([])

//     const [courses, setCourses] = useState(null)
//     const [approvedCourses, setApprovedCourses] = useState(null)
//     const [results, setResults] = useState(null)

//     const [currentCenter, setCurrentCenter] = useState(null)
//     const [nickname, setNickname] = useState(null)

//     const typePropositionValue = {
//         ZTUDENT: 1,
//         INSTRUCTOR: 2
//     }

//     const typeHelp = {
//         ZTUDENT: {
//             DASHBOARD: 1
//         },
//         INSTRUCTOR: {
//             DASHBOARD: 2
//         }
//     }

//     const GoToNextView = async (course, goToQuickAccessInstrument = false) => {
//         try {
//             const response = await SaveCourseToShow(session, course, goToQuickAccessInstrument)
            
//             if (response.success) {
//                 setSession(response.data)
//                 navigate('/course')
//             }
//         } catch (error) {
//             await LogErrors('GoToNextView - Ztudiant', error)
//             return { success: false, message: '' }
//         }
//     }

//     /**
//      * Obtiene un arreglo de objetos con los idCourse únicos
//      * @param {Array} evaluations - Lista de evaluaciones con propiedades idCourse
//      * @returns {Array<{idCourse: number}>} Lista de objetos únicos por idCourse
//      */
//     const GetListOfCourses = async (evaluations = []) => {
//         try {
//             const unique = new Set()
            
//             // Recorremos y agregamos solo los idCourse únicos al Set
//             evaluations.forEach(evaluation => {
//                 if (evaluation.idCourse !== undefined) {
//                     unique.add(evaluation.idCourse)
//                 }
//             })
            
//             // Convertimos el Set en arreglo de objetos
//             return Array.from(unique).map(idCourse => ({ idCourse }))
//         } catch (error) {
//             await LogErrors('GetListOfCourses - Ztudiant', error)
//         }
//     }

//     //Checar cómo mandar a llamar esta función para obtener e imprimir los cursos
//     const LoadPage = async () => {
//         try {
//             document.body.classList.add('BodyComplete')


//             const response = await GetCourses(session, centerSelected, roleSelected)
            
//             if (response.success) {    
//                 // setCourses(response.data?.user?.courses?.myCourses ?? [])
//                 // setApprovedCourses(response.data?.user?.courses?.myApprovedCourses ?? [])

//                 const myCourses = response.data?.user?.courses?.myCourses
//                 if (myCourses) {
//                     setCourses(myCourses)
//                 } else {
//                     setCourses(null)
//                 }

//                 const myApprovedCourses = response.data?.user?.courses?.myApprovedCourses
//                 if (myApprovedCourses) {
//                     setApprovedCourses(myApprovedCourses)
//                 } else {
//                     setApprovedCourses(null)
//                 }
                

//                 const results = await GetResults(response.data)
                
//                 if (results.success) {
//                     const evaluations = results?.data?.evaluations

//                     if (evaluations) {
//                         const listOfCourses = await GetListOfCourses(evaluations)

//                         setResults(listOfCourses)
//                         //setResults(results.data?.evaluations ?? [])
//                     } else {
//                         setResults(null)
//                     }
                    
//                     setSession(results.data)
//                 } else {
//                     setSession(response.data)
//                 }
//             } else {
//                 //Mostrar mensaje de que no se pudo obtener la info o el error
//                 // if (response.message !== undefined) {
//                 //     if (response.message !== '') {
//                 //         console.log(response?.message) //Mensaje temporal
//                 //     }
//                 // }
//                 console.log(response?.message)
                
                
//                 const results = await GetResults(session)
                
//                 if (results.success) {
//                     const evaluations = results?.data?.evaluations

//                     if (evaluations) {
//                         //setResults(results.data?.evaluations ?? [])
//                         setResults(evaluations)
//                     } else {
//                         setResults(null)
//                     }

//                     setSession(results.data)
//                 }
//             }


//             // Obtener info del centro actual para utilizar cierta info en la pantalla
//             const responseCenter = await BLGetCurrentCenter()
            
//             if (responseCenter.success) {
//                 let centerId = responseCenter?.data?.id

//                 if (!centerId) {
//                     return
//                 }

//                 // Si el dato viene como cadena, convertirlo a entero
//                 if (typeof centerId === "string") {
//                     centerId = parseInt(centerId)
//                 }
                
//                 responseCenter.data.id = centerId

//                 setCurrentCenter(responseCenter.data)
//             }


//             // Obtener info del alias del usuario para utilizar cierta info en la pantalla
//             const responseNickname = await BLGetNickname()
            
//             if (responseNickname.success) {
//                 const nickname = responseNickname?.data

//                 if (!nickname) {
//                     return
//                 }

//                 setNickname(nickname)
//             }
//         } catch (error) {
//             await LogErrors('LoadPage - Ztudiant', error)
//         } finally {
//             setIsLoading(false)
//         }
//     }

//     /**
//      * Función para obtener la configuración, una vez que termine de cargarse el componente
//      */
//     useEffect(() => {
//         LoadPage()
//     }, []) // El array vacío [] asegura que useEffect se ejecute solo una vez, al montar

    
//     return (
//         <>
//             {isLoading ? (
//                 <Loading />
//             ) : (
//                 <BodyWithSideMenu page={WEBPAGES.ZTUDENT.DASHBOARD} theme={theme} propositionValue={TYPE_PROPOSITION_VALUE.ZTUDENT} idHelp={HELP_TYPE.ZTUDENT.DASHBOARD} role={ROLES.ZTUDENT}>
//                     {/* <Header theme={theTheme} setTheme={setTheme} propositionValue={typePropositionValue.ZTUDENT} idHelp={HELP_TYPE.ZTUDENT.DASHBOARD} /> */}
//                     <Header theme={theme} setTheme={setTheme} propositionValue={TYPE_PROPOSITION_VALUE.ZTUDENT} idHelp={HELP_TYPE.ZTUDENT.DASHBOARD} />
                    
//                     <main className='Container Dashboard--V2 Container-FullHeight Fonts--PlusJakartaSans'>
//                         <p className='Dashboard--V2__Title'>{translate('labels.dashboard.courses.welcome')} <span className='Dashboard--V2__Text--Highlight'>{(nickname && nickname !== '') ? nickname : `${session.user.name} ${session.user.lastName}`} <i className='bi bi-emoji-smile'></i></span></p>
//                         {session?.navigation?.center?.name && (
//                             <p className='Dashboard--V2__Text Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Centro: {session.navigation.center.name}</p>
//                         )}
//                         {session?.navigation?.username && (
//                             <p className='Dashboard--V2__Text Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Usuario: {session.navigation.username}</p>
//                         )}
                        
//                         <p className='Dashboard--V2__SpaceUp'>Ingresa a la sección que desees</p>

//                         <section className='Dashboard--V2__SpaceUp Dashboard--V2__Sections'>
//                             {courses && (
//                                 courses.length > 0 ? (
//                                     <>
//                                         <p className='Dashboard--V2__Sections__Title'>Celebremos el día del estudiante</p>
//                                         <p className='Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Con la dinámica que tenemos preparada para ti</p>
                                        
//                                         <div className='Dashboard--V2__Sections__List'>
//                                             <div className='Dashboard--V2__Sections__List__Items--Ztudent Dashboard--V2__Sections__List__Items--Gray'> {/* onClick={() => GetZtudentDataToGames()} */}
//                                                 <p className='Dashboard--V2__Sections__List__Items--Ztudent__Subtitle'><i className='bi bi-controller'></i> Dinámicas</p>
//                                                 <p className='Dashboard--V2__Sections__List__Items--Ztudent__Title'>Tiro a gol (1 vs 1)</p>
//                                                 <p className='Dashboard--V2__Sections__List__Items--Ztudent__Title small mb-0 pb-0 h-auto'>Próximamente</p>
                                                
//                                                 <button type='button' className='Btn__Ztudia--PrimaryOutline Dashboard--V2__Sections__List__Items--Ztudent__Buttons'> {/* onClick={() => GetZtudentDataToGames()} */}
//                                                     {/* <i className='bi bi-arrow-right'></i> */}
//                                                     <i className='bi bi-lock-fill'></i>
//                                                 </button>
//                                             </div>
//                                         </div>
//                                     </>
//                                 ) : (
//                                     <p>{translate('labels.dashboard.courses.empty')}</p>
//                                 )
//                             )}
//                         </section>

//                         <section className='Dashboard--V2__SpaceUp Dashboard--V2__Sections'>
//                             {courses && (
//                                 courses.length > 0 ? (
//                                     <>
//                                         <p className='Dashboard--V2__Sections__Title'>{translate('labels.dashboard.courses.title')}</p>
//                                         <p className='Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Total: {courses.length}</p>

//                                         <div className='Dashboard--V2__Sections__List'>
//                                             {courses.map((course, index) => (
//                                                 course && (
//                                                     <div key={index} className='Dashboard--V2__Sections__List__Items--Ztudent Dashboard--V2__Sections__List__Items--Pink' onClick={() => GoToNextView(course)}>
//                                                         {course.tileIconClass && course.tileIconClass !== '' && (
//                                                             <figure className='Dashboard--V2__Sections__List__Items--Ztudent__Icon'>
//                                                                 <i className={`${course.tileIconClass}`}></i>
//                                                             </figure>
//                                                         )}

//                                                         <p className='Dashboard--V2__Sections__List__Items--Ztudent__Subtitle'><i className='bi bi-file-earmark'></i> {course.type === 1 ? translate('labels.dashboard.tiles.examType.certification') : translate('labels.dashboard.tiles.examType.custom')}</p>
//                                                         <p className='Dashboard--V2__Sections__List__Items--Ztudent__Title'>{`${course.name}${course?.level && ` nivel ${course.level}` }`}</p>

//                                                         <button type='button' className='Btn__Ztudia--PrimaryOutline Dashboard--V2__Sections__List__Items--Ztudent__Buttons' onClick={() => GoToNextView(course)}>
//                                                             <i className='bi bi-arrow-right'></i>
//                                                         </button>
//                                                     </div>
//                                                 )
//                                             ))}
//                                         </div>
//                                     </>
//                                 ) : (
//                                     <p>{translate('labels.dashboard.courses.empty')}</p>
//                                 )
//                             )}
//                         </section>

//                         <section className='Dashboard--V2__SpaceUp Dashboard--V2__Sections'>
//                             {results && (
//                                 results.length > 0 ? (
//                                     <>
//                                         <p className='Dashboard--V2__Sections__Title'>Mis resultados</p>
//                                         <p className='Dashboard--V2__Text--Small Dashboard--V2__Text--Hide'>Total: {results.length}</p>

//                                         <div className='Dashboard--V2__Sections__List'>
//                                             {results.map((result, index) => (
//                                                 result && (
//                                                     <Result key={index} session={session} result={result} />
//                                                 )
//                                             ))}
//                                         </div>
//                                     </>
//                                 ) : (
//                                     <p>Actualmente no tienes resultados.</p>
//                                 )
//                             )}
//                         </section>
//                     </main>
//                 </BodyWithSideMenu>
//             )}
//         </>
//     )
// }

// export { Ztudiant };









{/* <Section textSection={translate('labels.dashboard.courses.title')}>
    {courses && (
        courses.length > 0 ? (
            <Tile elements={courses} session={session} setSession={setSession} />
        ) : (
            <p>{translate('labels.dashboard.courses.empty')}</p>
        )
    )}
</Section>

{approvedCourses && (
    //approvedCourses.length >= 0 && (
        approvedCourses.length > 0 && (
            <Section textSection={translate('labels.dashboard.approvedCourses.title')} classNameSection={'Section-Separation'}>
                <Tile elements={approvedCourses} session={session} setSession={setSession} />
            </Section>
        )
    //)
)}

{results && (
    results.length > 0 ? (
        <Section textSection="Mis resultados" classNameSection={'Section-Separation'}>
            <div className='Tiles HideMoveButton'>
                <Button className={'Tiles-ButtonMoveTile'} iconClass={'bi bi-three-dots-vertical'} />

                {results.map((result, index) => (
                    <Result key={index} session={session} result={result} />
                ))}
            </div>
        </Section>
    ) : (
        <p>Actualmente no tienes resultados.</p>
    )
)} */}